import { Button, CircularProgress, useTheme } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

const animateScaleX = keyframes`
  0% {
    transform: scaleX(1);
  }
  50% {
    transform: scaleX(1.05);
  }
  100% {
    transform: scaleX(1);
  }
`;

const OutlinedButtonWrapper = styled.div`
	display: inline-block;
`;

const StyledOutlinedButton = styled(Button)`
	&& {
		background: transparent;
		border: 1px solid ${props => props.themeColor};
		color: ${props => props.themeColor} !important;
		outline: none;
		padding: 18px 20px;
		height: 30px;
		border-radius: 10px;
		overflow: hidden;
		transform: scaleX(1);
		transition: transform 0.5s cubic-bezier(0.4, 0, 0, 1);
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
		min-width: ${props => props.minWidth || 'auto'};
		width: ${props => (props.fullWidth ? '100%' : 'auto')};
		opacity: ${props => (props.disabled ? 0.6 : 1)};
		pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
		text-transform: none;

		&:hover {
			animation: ${animateScaleX} 0.6s cubic-bezier(0.4, 0, 0, 1);
			background: transparent;
			color: ${props => props.isDarkMode ? '#000' : '#fff'} !important;
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			bottom: 0;
			background: ${props => props.isDarkMode ? '#fff' : '#000'};
			transition: transform 0.5s cubic-bezier(0.4, 0, 0, 1), border-radius 0.5s cubic-bezier(0.4, 0, 0, 1);
			width: 100%;
			height: 100%;
			border-radius: 50% 50% 0 0;
			transform: translateY(100%);
			z-index: -1;
		}

		&:hover::after {
			transform: translateY(0%);
			border-radius: 0;
		}

		&:focus {
			outline: none;
		}

		.MuiButton-label {
			position: relative;
			z-index: 99;
			color: ${props => props.themeColor};
			display: flex;
			align-items: center;
			gap: 2px;
			transition: color 0.5s cubic-bezier(0.4, 0, 0, 1);
		}

		&:hover .MuiButton-label {
			color: ${props => props.isDarkMode ? '#000' : '#fff'};
			z-index: 99;
		}

		.MuiButton-startIcon,
		.MuiButton-endIcon {
			position: relative;
			z-index: 1;
			transition: color 0.5s cubic-bezier(0.4, 0, 0, 1);
		}

		&:hover .MuiButton-startIcon,
		&:hover .MuiButton-endIcon {
			color: ${props => props.isDarkMode ? '#000' : '#fff'};
		}
        .omni-loader {
			color: ${props => props.themeColor} !important;
		}
	}
`;

const ButtonOmni = ({
	label,
	onClick,
	variant = 'outlined',
	color = 'primary',
	size = 'medium',
	fullWidth = false,
	startIcon = null,
	endIcon = null,
	loading = false,
	disabled = false,
	style = {},
	className = '',
	disableSpecialStyling = false,
	...otherProps
}) => {
	// Use theme hooks instead of localStorage
	const theme = useTheme();
	const mainTheme = useSelector(({ fuse }) => fuse.settings.mainTheme);

	// Get theme color from theme object with fallback to white
	// You can adjust the path based on your actual theme structure
	const themeColor = theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff';

	const isSelectButton = endIcon && !loading;
	const shouldApplySpecialStyling = variant === 'outlined' && !isSelectButton && !disableSpecialStyling;
	const isDarkMode = mainTheme === 'mainThemeDark' || theme.palette.type === 'dark';

	if (shouldApplySpecialStyling) {
		return (
			<OutlinedButtonWrapper style={style} className={className}>
				<StyledOutlinedButton
					onClick={onClick}
					variant={variant}
					color={color}
					size={size}
					fullWidth={fullWidth}
					startIcon={startIcon}
					endIcon={endIcon}
					disabled={loading || disabled}
					themeColor={themeColor}
					isDarkMode={isDarkMode}
					{...otherProps}
				>
					{loading ? (
						<CircularProgress
							size={20}
							className="omni-loader"
						/>
					) : (
						label
					)}
				</StyledOutlinedButton>
			</OutlinedButtonWrapper>
		);
	}

	return (
		<Button
			onClick={onClick}
			variant={variant}
			color={color}
			size={size}
			fullWidth={fullWidth}
			startIcon={startIcon}
			endIcon={endIcon}
			disabled={loading || disabled}
			style={{ textTransform: 'none', ...style }}
			className={`shimmer-button ${className}`}
			{...otherProps}
		>
			{loading ? <CircularProgress size={20} /> : label}
		</Button>
	);
};

export default ButtonOmni;